import React from "react";
import gsap from "gsap";
import {
  CircleArrowUpIcon,
  CirclePlusIcon,
  CreditCardIcon,
  LandmarkIcon,
} from "lucide-react";

import UsdcStrokeIcon from "@/assets/svg/usdc-stroke.svg?react";
import ApplePayIcon from "@/assets/svg/apple-pay.svg?react";
import GooglePayIcon from "@/assets/svg/google-pay.svg?react";
import { cn } from "@/helpers/tailwind";
import { WalletQuickInfo } from "@/pages/wallet/components";
import { WalletType } from "@/types";
import { Button, useCarousel } from "@/components/ui";

const depositOptions = [
  {
    title: "Credit/Debit Card",
    icon: <CreditCardIcon className="size-5 lg:size-8" />,
  },
  {
    title: "Bank Transfer",
    icon: <LandmarkIcon className="size-5 lg:size-8" />,
  },
  {
    title: "Google Pay",
    icon: (
      <div
        className={cn(
          "h-5 w-min rounded-full border px-2 py-1 lg:h-8",
          "flex items-center justify-center",
          "border-indigo-400 bg-indigo-400/10"
        )}>
        <GooglePayIcon className="aspect-[2/1] h-4 lg:h-5" />
      </div>
    ),
  },
  {
    title: "Apple Pay",
    icon: (
      <div
        className={cn(
          "h-5 w-min rounded-full border px-2 py-1 text-white lg:h-8",
          "flex items-center justify-center",
          "border-indigo-400 bg-indigo-400/10"
        )}>
        <ApplePayIcon className="aspect-[2/1] h-4 lg:h-5" />
      </div>
    ),
  },
];

interface OnboardingStep3Props {
  variant?: "desktop" | "mobile";
}

export const OnboardingStep3: React.FC<OnboardingStep3Props> = ({
  variant = "mobile",
}) => {
  const { api } = useCarousel();
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
  const depositMethodsRef = React.useRef<HTMLDivElement | null>(null);
  const instructionsRef = React.useRef<HTMLDivElement | null>(null);
  const walletInfoRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!api) return;

    // Create the timeline once
    if (!timelineRef.current) {
      timelineRef.current = gsap.timeline({ paused: true }).fromTo(
        [
          depositMethodsRef.current,
          instructionsRef.current,
          walletInfoRef.current,
        ],
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.5,
          ease: "power4.out",
        }
      );
    }

    const handleSlideChange = () => {
      const currentSlide = api.selectedScrollSnap();

      if (currentSlide === 2) {
        timelineRef.current?.play(0);
      } else {
        timelineRef.current?.reverse();
      }
    };

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
      timelineRef.current?.kill();
    };
  }, [api]);

  return (
    <div
      className={cn(
        "w-full rounded-3xl bg-slate-900 p-3",
        "flex flex-col items-center justify-between gap-3",
        { "flex-row gap-4 p-6": variant === "desktop" }
      )}>
      <div
        className={cn("border-slate-700", {
          "w-full border-b pb-4": variant === "mobile",
          "border-r pr-8": variant === "desktop",
        })}
        ref={depositMethodsRef}>
        <p className="font-semibold">Deposit using:</p>
        <div className="mt-3 grid grid-cols-[minmax(132px,1fr)_minmax(132px,1fr)] gap-3">
          {depositOptions.map(({ title, icon }, index) => (
            <div
              key={index}
              className={cn(
                "border border-indigo-400 bg-indigo-400/10 text-indigo-400",
                "flex flex-col rounded-lg",
                {
                  "p-2": variant === "mobile",
                  "min-h-20 p-3": variant === "desktop",
                }
              )}>
              {icon}
              <p
                className={cn("text-indigo-400", {
                  "mt-1.5 text-sm": variant === "mobile",
                  "mt-3": variant === "desktop",
                })}>
                {title}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div
        className="flex flex-grow flex-col gap-2.5 lg:gap-2"
        ref={instructionsRef}>
        <p className="font-geist text-base font-semibold lg:text-xl">
          Ready to withdraw?
        </p>
        <p className="font-geist text-xs lg:text-sm">
          Simply enter the amount and your preferred withdrawal method.
          <br />
          Funds will be sent to your linked account.
        </p>
        {variant === "desktop" && (
          <div className="my-4 border-b border-slate-700" />
        )}
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-indigo-400/20 p-1.5 text-indigo-400 lg:p-2">
            <UsdcStrokeIcon className="size-4 lg:size-5" />
          </div>
          <p className="text-xs text-slate-400 lg:text-base">
            All transactions are in USDC.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-auto" ref={walletInfoRef}>
        <WalletQuickInfo
          className="pointer-events-none mb-2"
          balance="128500"
          walletAddress="0x12xxxxxxc789"
          walletType={WalletType.SMART_ACCOUNT}
          ticketSummary={[{ amount: 5, count: 2 }]}
          isLoading={false}
          hideProfileSummary={variant === "mobile"}
        />
        <div className="grid grid-cols-2 gap-1">
          <Button
            size={variant === "mobile" ? "sm" : "default"}
            variant="tertiary"
            className="pointer-events-none">
            <CirclePlusIcon />
            <span>Deposit</span>
          </Button>
          <Button
            size={variant === "mobile" ? "sm" : "default"}
            variant="tertiary"
            className="pointer-events-none">
            <CircleArrowUpIcon className="rotate-45" />
            <span>Withdraw</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
